import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Logo from "../assets/logo.png";

const API_BASE = "http://nissi-india.com";

const Login = () => {
  const navigate = useNavigate();

  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [finYearId, setFinYearId] = useState("");
  const [branchId, setBranchId] = useState("");

  const [finYears, setFinYears] = useState([]);
  const [branches, setBranches] = useState([]);

  const [loadingYears, setLoadingYears] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const selectedFinYearText = useMemo(() => {
    const fy = finYears.find((x) => String(x.fld_id) === String(finYearId));
    return fy?.fld_financialyear || "";
  }, [finYearId, finYears]);

  const selectedBranchName = useMemo(() => {
    const b = branches.find((x) => String(x.fld_branchid) === String(branchId));
    return b?.fld_branchname || "";
  }, [branchId, branches]);

  useEffect(() => {
    (async () => {
      try {
        setError("");
        setLoadingYears(true);

        const rs = await axios.get(`${API_BASE}/api/module/finance-years`);
        if (!rs.data?.status) throw new Error(rs.data?.message || "Failed to load financial years");

        const rows = rs.data.data || [];
        setFinYears(rows);

        const active = rows.find((x) => x.fld_active === 1 || x.fld_active === true);
        const pick = active || rows[0];
        if (pick) setFinYearId(String(pick.fld_id));
      } catch (e) {
        setError(e?.response?.data?.message || e.message || "Failed to load financial years");
      } finally {
        setLoadingYears(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!finYearId) return;

    (async () => {
      try {
        setError("");
        setLoadingBranches(true);
        setBranches([]);
        setBranchId("");

        const rs = await axios.get(`${API_BASE}/api/module/branches`, {
          params: { finYearId },
        });

        if (!rs.data?.status) throw new Error(rs.data?.message || "Failed to load branches");

        const rows = rs.data.data || [];
        setBranches(rows);
      } catch (e) {
        setError(e?.response?.data?.message || e.message || "Failed to load branches");
      } finally {
        setLoadingBranches(false);
      }
    })();
  }, [finYearId]);

  const validate = () => {
    if (!userName.trim()) return "Please enter User Name..";
    if (!password.trim()) return "Please enter Password..";
    if (!branchId) return "Please Select Branch..";
    if (!finYearId) return "Please Select Financial Year..";
    return "";
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setError("");

    const msg = validate();
    if (msg) {
      setError(msg);
      return;
    }

    try {
      setSubmitting(true);

      const rs = await axios.post(`${API_BASE}/api/module/login`, {
        finYearId: Number(finYearId),
        branchId: Number(branchId),
        username: userName.trim(),
        password: password,
      });

      if (!rs.data?.status) {
        setError(rs.data?.message || "Invalid Username or Password!");
        return;
      }

      const session = rs.data.session;

      // ---- Branch Logic ----
      const branchValue =
        selectedBranchName?.toLowerCase() === "headoffice" ||
        selectedBranchName?.toLowerCase() === "head office" ||
        String(branchId) === "8"
          ? "HO"
          : "branch";
      // ----------------------

      localStorage.setItem(
        "moduleSession",
        JSON.stringify({
          ...session,
          branchName: selectedBranchName,
          finYearText: selectedFinYearText,
        })
      );

      localStorage.setItem("finYearId", String(finYearId));
      localStorage.setItem("finYear", String(selectedFinYearText));
      localStorage.setItem("branchId", String(branchId));
      localStorage.setItem("branchType", branchValue);
      localStorage.setItem("fyr", String(selectedFinYearText));
      localStorage.setItem("username", session?.user?.username || "");
      localStorage.setItem("userId", session?.user?.id || "");

      if (rs.data.otpRequired) {
        navigate("/otp");
      } else {
        navigate("/temporary-home");
      }
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Login failed. Please try again.");
      console.error("Login Error:", e);
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <div className="min-h-screen w-full bg-slate-950 relative overflow-hidden">
      {/* Background glow */}
      <div className="absolute inset-0">
        <div className="absolute -top-40 -left-40 h-96 w-96 rounded-full bg-indigo-600/20 blur-3xl" />
        <div className="absolute top-20 -right-40 h-[28rem] w-[28rem] rounded-full bg-cyan-500/20 blur-3xl" />
        <div className="absolute bottom-0 left-1/3 h-80 w-80 rounded-full bg-fuchsia-500/10 blur-3xl" />
      </div>

      <div className="relative min-h-screen flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-5xl grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-3xl overflow-hidden shadow-2xl border border-white/10 bg-white/5 backdrop-blur-xl">
          {/* Left branding panel */}
          <div className="hidden lg:flex flex-col justify-between p-10 bg-gradient-to-br from-white/10 to-white/0">
            <div>
              <div className="flex items-center gap-3">
                <img src={Logo} alt="Logo" className="h-12 w-auto" />
                <div>
                  <div className="text-white text-xl font-semibold tracking-wide">Infraconsoft</div>
                  <div className="text-white/60 text-sm">Module Login</div>
                </div>
              </div>

              <div className="mt-10">
                <h1 className="text-3xl font-bold text-white leading-tight">
                  Welcome back.
                  <br />
                  Sign in to continue.
                </h1>
                <p className="mt-4 text-white/70 text-sm leading-relaxed">
                  Select your financial year and branch, then enter your credentials. Your selection will be used to
                  connect the correct tenant databases for the module.
                </p>
              </div>

              <div className="mt-10 grid grid-cols-2 gap-4">
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <div className="text-white/60 text-xs">Security</div>
                  <div className="text-white text-sm font-medium mt-1">Tenant-based login</div>
                  <div className="text-white/60 text-xs mt-2">Financial year controls DB selection.</div>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <div className="text-white/60 text-xs">Access</div>
                  <div className="text-white text-sm font-medium mt-1">Branch aware</div>
                  <div className="text-white/60 text-xs mt-2">Validates branch + user setup.</div>
                </div>
              </div>
            </div>

            <div className="text-white/50 text-xs">
              &copy; {new Date().getFullYear()} Infraconsoft. All rights reserved.
            </div>
          </div>

          {/* Right form panel */}
          <div className="p-8 sm:p-10 bg-white/5">
            {/* Mobile header */}
            <div className="lg:hidden flex items-center gap-3 mb-8">
              <img src={Logo} alt="Logo" className="h-10 w-auto" />
              <div>
                <div className="text-white text-lg font-semibold">Infraconsoft</div>
                <div className="text-white/60 text-xs">Module Login</div>
              </div>
            </div>

            <div className="mb-6">
              <h2 className="text-2xl font-bold text-white">Sign in</h2>
              <p className="text-white/60 text-sm mt-1">Use your credentials to access the module.</p>
            </div>

            {error ? (
              <div className="mb-5 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-red-200 text-sm">
                {error}
              </div>
            ) : null}

            <form onSubmit={handleLogin} className="space-y-4">
              {/* Username */}
              <div>
                <label className="block text-white/70 text-xs mb-2">User Name *</label>
                <input
                  type="text"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  placeholder="Enter your user name"
                  autoComplete="username"
                  className="w-full px-4 py-3 rounded-2xl bg-white/10 text-white placeholder:text-white/40 border border-white/10 focus:outline-none focus:ring-2 focus:ring-cyan-400/40"
                />
              </div>

              {/* Password */}
              <div>
                <label className="block text-white/70 text-xs mb-2">Password *</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  className="w-full px-4 py-3 rounded-2xl bg-white/10 text-white placeholder:text-white/40 border border-white/10 focus:outline-none focus:ring-2 focus:ring-cyan-400/40"
                />
              </div>

              {/* Financial Year */}
              <div>
                <label className="block text-white/70 text-xs mb-2">Financial Year *</label>
                <select
                  value={finYearId}
                  onChange={(e) => setFinYearId(e.target.value)}
                  disabled={loadingYears}
                  className="w-full px-4 py-3 rounded-2xl bg-white/10 text-white border border-white/10 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 disabled:opacity-60"
                >
                  {loadingYears ? (
                    <option value="">Loading...</option>
                  ) : finYears.length ? (
                    finYears.map((fy) => (
                      <option key={fy.fld_id} value={fy.fld_id} className="text-black">
                        {fy.fld_financialyear}
                        {fy.fld_active === 1 || fy.fld_active === true ? " (Active)" : ""}
                      </option>
                    ))
                  ) : (
                    <option value="">No Financial Years</option>
                  )}
                </select>
                {selectedFinYearText ? (
                  <div className="mt-2 text-white/40 text-xs">Selected: {selectedFinYearText}</div>
                ) : null}
              </div>

              {/* Branch */}
              <div>
                <label className="block text-white/70 text-xs mb-2">Branch *</label>
                <select
                  value={branchId}
                  onChange={(e) => setBranchId(e.target.value)}
                  disabled={!finYearId || loadingBranches}
                  className="w-full px-4 py-3 rounded-2xl bg-white/10 text-white border border-white/10 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 disabled:opacity-60"
                >
                  {!finYearId ? (
                    <option value="">Select Financial Year first</option>
                  ) : loadingBranches ? (
                    <option value="">Loading branches...</option>
                  ) : (
                    <>
                      <option value="">Select</option>
                      {branches.map((b) => (
                        <option key={b.fld_branchid} value={b.fld_branchid} className="text-black">
                          {b.fld_branchname}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full mt-2 py-3 rounded-2xl font-semibold text-sm text-white bg-gradient-to-r from-cyan-500/90 to-indigo-500/90 hover:from-cyan-500 hover:to-indigo-500 transition shadow-lg shadow-cyan-500/10 disabled:opacity-60"
              >
                {submitting ? "Signing in..." : "Login"}
              </button>

              <div className="pt-3 text-center text-xs text-white/40">
                Registered product of Nissi Infotech Private Limited
              </div>
            </form>

            {/* Mobile footer */}
            <div className="lg:hidden mt-8 text-center text-white/40 text-xs">
              &copy; {new Date().getFullYear()} Infraconsoft. All rights reserved.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
